// src/pages/hr/recruitment/RecruitmentDashboard.jsx
import React from "react";
import { useNavigate } from "react-router-dom";
import { useRecruitmentContext } from "../../../context/RecruitmentContext";
import { useOnboardingCandidates } from "../../../hooks/useRecruitment";
import {
  UserPlus,
  ShieldCheck,
  CheckCircle2,
  ArrowRight,
  Clock,
  UserCheck,
  FileText,
  Calendar,
  Briefcase,
  AlertCircle,
} from "lucide-react";

const CHECKLIST_ITEMS = [
  "Send Offer letter",
  "Inform Pooja",
  "Onboarding form",
  "Inform Team lead & sharuk sir",
  "Add to main group",
  "Adding in team group",
  "Send google meet link",
  "Add in the google meet attendence class",
  "Update in Enginneering tracker",
  "Add in Shift roaster",
  "Update the Current employees list",
  "Add in EMS",
  "Add in Zoho cliq",
  "Add in google meet",
];

function computeProgress(candidate) {
  if (typeof candidate?.onboarding?.progress === "number") {
    return candidate.onboarding.progress;
  }
  const checklist = candidate?.onboarding?.checklist || {};
  if (!checklist || Object.keys(checklist).length === 0) return 0;
  const done = CHECKLIST_ITEMS.filter((item) => checklist[item]).length;
  return Math.round((done / CHECKLIST_ITEMS.length) * 100);
}

function BgvBadge({ status }) {
  const s = (status || "Not Started").toLowerCase();
  let bg = "#f1f5f9";
  let color = "#475569";
  let border = "#cbd5e1";

  if (s.includes("cleared") || s.includes("complete") || s.includes("verified")) {
    bg = "#ecfdf5";
    color = "#047857";
    border = "#a7f3d0";
  } else if (s.includes("progress") || s.includes("initiated")) {
    bg = "#fef3c7";
    color = "#b45309";
    border = "#fde68a";
  }

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.25rem",
        padding: "0.2rem 0.55rem",
        borderRadius: 99,
        fontSize: "0.72rem",
        fontWeight: 700,
        background: bg,
        color: color,
        border: `1px solid ${border}`,
      }}
    >
      <ShieldCheck size={12} strokeWidth={2} /> BGV: {status || "Not Started"}
    </span>
  );
}

export default function RecruitmentDashboard() {
  const navigate = useNavigate();
  const {
    jobOpenings,
    candidates,
    offers,
    interviews,
    activityLogs,
    stages,
    stageColors,
  } = useRecruitmentContext();

  const { data: onboardingCandidates = [], loading: onboardingLoading } = useOnboardingCandidates();

  const openJobs = jobOpenings.filter((j) => j.status === "Open");
  const activeCandidates = candidates.filter(
    (c) => !["Rejected", "Withdrawn", "Hired", "Moved to Onboarding"].includes(c.stage)
  );
  const hiredCount = candidates.filter((c) => c.stage === "Hired").length;
  const offerCount = offers.length;
  const acceptedOffers = offers.filter((o) => o.status === "Accepted").length;

  const todayStr = new Date().toISOString().split("T")[0];
  const todayInterviews = interviews.filter(
    (i) => i.scheduledDate === todayStr || i.date === todayStr
  );

  // Department breakdown
  const deptCounts = {};
  jobOpenings.forEach((j) => {
    const dept = j.department || "General";
    deptCounts[dept] = (deptCounts[dept] || 0) + 1;
  });
  const deptEntries = Object.entries(deptCounts);

  // Stage distribution (including Onboarding)
  const allStages = stages.includes("Onboarding")
    ? stages
    : [...stages.filter((s) => s !== "Hired"), "Onboarding", "Hired"];

  const stageCounts = {};
  allStages.forEach((s) => {
    if (s === "Onboarding") {
      stageCounts[s] = candidates.filter(
        (c) => c.stage === "Moved to Onboarding" || c.stage === "Onboarding" || c.status === "Onboarding"
      ).length;
    } else {
      stageCounts[s] = candidates.filter((c) => c.stage === s).length;
    }
  });

  // Onboarding metrics
  const totalInOnboarding = onboardingCandidates.length;
  const clearedBgvCount = onboardingCandidates.filter((c) => {
    const s = (c.onboarding?.bgvStatus || "").toLowerCase();
    return s.includes("cleared") || s.includes("complete") || s.includes("verified");
  }).length;
  const inProgressBgvCount = onboardingCandidates.filter((c) => {
    const s = (c.onboarding?.bgvStatus || "").toLowerCase();
    return s.includes("progress") || s.includes("initiated");
  }).length;
  const notStartedBgvCount = totalInOnboarding - clearedBgvCount - inProgressBgvCount;

  const avgProgress = totalInOnboarding > 0
    ? Math.round(
        onboardingCandidates.reduce((acc, c) => acc + computeProgress(c), 0) / totalInOnboarding
      )
    : 0;

  return (
    <div className="ats-dashboard" style={{ display: "flex", flexDirection: "column", gap: "1.5rem" }}>
      <style>{`
        .ats-kpi-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: 1rem; }
        .ats-kpi-card { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.1rem 1.2rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); }
        .ats-kpi-val { font-size: 1.8rem; font-weight: 800; color: var(--text-primary, #111); line-height: 1.1; }
        .ats-kpi-lbl { font-size: 0.78rem; font-weight: 600; color: var(--text-muted, #6b7280); margin-top: 4px; }
        .ats-kpi-sub { font-size: 0.72rem; color: #10b981; margin-top: 4px; font-weight: 500; }
        .ats-grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 1.25rem; }
        @media (max-width: 850px) { .ats-grid-2 { grid-template-columns: 1fr; } }
        .ats-panel { background: var(--bg-card, #fff); border: 1px solid var(--border, #e5e7eb); border-radius: 12px; padding: 1.25rem 1.5rem; box-shadow: 0 1px 5px rgba(0,0,0,0.05); }
        .ats-panel-title { font-size: 0.95rem; font-weight: 700; color: var(--text-primary, #111); margin-bottom: 1rem; display: flex; justify-content: space-between; align-items: center; }
        .funnel-bar-row { display: flex; align-items: center; gap: 0.75rem; margin-bottom: 0.6rem; font-size: 0.82rem; }
        .funnel-lbl { width: 140px; font-weight: 600; color: var(--text-primary, #111); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .funnel-track { flex: 1; height: 10px; background: #f3f4f6; border-radius: 99px; overflow: hidden; }
        .funnel-fill { height: 100%; border-radius: 99px; transition: width 0.4s; }
        .funnel-cnt { width: 32px; text-align: right; font-weight: 700; font-size: 0.8rem; }
        .activity-row { display: flex; gap: 0.75rem; align-items: flex-start; padding: 0.65rem 0; border-bottom: 1px solid var(--border, #f3f4f6); font-size: 0.82rem; }
        .activity-row:last-child { border-bottom: none; }
        .activity-dot { width: 8px; height: 8px; border-radius: 50%; background: #1b4b31; margin-top: 5px; flex-shrink: 0; }
        .activity-time { font-size: 0.7rem; color: var(--text-muted, #6b7280); }
        
        .onboarding-card-item {
          background: #ffffff;
          border: 1px solid #e2e8f0;
          border-left: 4px solid #10b981;
          border-radius: 12px;
          padding: 0.9rem 1.1rem;
          display: flex;
          flex-direction: column;
          gap: 0.65rem;
          transition: all 0.2s ease;
        }
        .onboarding-card-item:hover {
          box-shadow: 0 4px 14px rgba(16, 185, 129, 0.1);
          border-color: #a7f3d0;
          border-left-color: #059669;
        }
      `}</style>

      {/* KPI Cards */}
      <div className="ats-kpi-grid">
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #1b4b31" }}>
          <div className="ats-kpi-val">{openJobs.length}</div>
          <div className="ats-kpi-lbl">Open Requisitions</div>
          <div className="ats-kpi-sub" style={{ color: "#1b4b31" }}>Across {deptEntries.length} departments</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #1b4b31" }}>
          <div className="ats-kpi-val">{activeCandidates.length}</div>
          <div className="ats-kpi-lbl">Active Candidates</div>
          <div className="ats-kpi-sub" style={{ color: "#1b4b31" }}>{candidates.length} total applications</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #1b4b31" }}>
          <div className="ats-kpi-val">{todayInterviews.length}</div>
          <div className="ats-kpi-lbl">Interviews Today</div>
          <div className="ats-kpi-sub" style={{ color: "#1b4b31" }}>{interviews.length} total scheduled</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #1b4b31" }}>
          <div className="ats-kpi-val">{offerCount}</div>
          <div className="ats-kpi-lbl">Offers Released</div>
          <div className="ats-kpi-sub" style={{ color: "#1b4b31" }}>{acceptedOffers} accepted</div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #10b981", background: "#f0fdf4" }}>
          <div className="ats-kpi-val" style={{ color: "#065f46" }}>{totalInOnboarding}</div>
          <div className="ats-kpi-lbl" style={{ color: "#047857", fontWeight: 700 }}>In Onboarding</div>
          <div className="ats-kpi-sub" style={{ color: "#059669" }}>
            Active joiners tracking
          </div>
        </div>
        <div className="ats-kpi-card" style={{ borderTop: "3px solid #1b4b31" }}>
          <div className="ats-kpi-val">{hiredCount}</div>
          <div className="ats-kpi-lbl">Total Hired</div>
          <div className="ats-kpi-sub" style={{ color: "#1b4b31" }}>Success conversion</div>
        </div>
      </div>

      {/* ── Featured Section: Employee Onboarding Status ── */}
      <div className="ats-panel" style={{ border: "1.5px solid #a7f3d0", background: "#ffffff" }}>
        <div className="ats-panel-title" style={{ borderBottom: "1px solid #f1f5f9", paddingBottom: "0.75rem", marginBottom: "1rem" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "0.6rem" }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#ecfdf5", color: "#059669", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <UserPlus size={18} />
            </div>
            <div>
              <span style={{ fontSize: "1rem", fontWeight: 800, color: "#0f172a" }}>Employee Onboarding Status</span>
              <div style={{ fontSize: "0.74rem", color: "#64748b", fontWeight: 500 }}>
                Live tracking of upcoming joiners, checklist completion, and background verification
              </div>
            </div>
          </div>
          <button
            type="button"
            className="btn-secondary"
            onClick={() => navigate("/hr/onboarding")}
            style={{
              padding: "0.4rem 0.85rem",
              fontSize: "0.78rem",
              fontWeight: 700,
              borderRadius: 8,
              display: "inline-flex",
              alignItems: "center",
              gap: "0.4rem",
              background: "#1b4b31",
              color: "#ffffff",
              border: "none",
              cursor: "pointer",
            }}
          >
            Open Onboarding Module <ArrowRight size={14} />
          </button>
        </div>

        {/* Quick Metrics Bar */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: "0.75rem", marginBottom: "1.1rem" }}>
          <div style={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 10, padding: "0.65rem 0.85rem" }}>
            <div style={{ fontSize: "0.72rem", color: "#64748b", fontWeight: 600 }}>Total New Joiners</div>
            <div style={{ fontSize: "1.25rem", fontWeight: 800, color: "#0f172a", marginTop: 2 }}>{totalInOnboarding}</div>
          </div>
          <div style={{ background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "0.65rem 0.85rem" }}>
            <div style={{ fontSize: "0.72rem", color: "#92400e", fontWeight: 600 }}>BGV Pending / In Progress</div>
            <div style={{ fontSize: "1.25rem", fontWeight: 800, color: "#b45309", marginTop: 2 }}>{notStartedBgvCount + inProgressBgvCount}</div>
          </div>
        </div>

        {/* Joiner Cards List */}
        {onboardingLoading ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "var(--text-muted)", fontSize: "0.85rem" }}>
            Loading onboarding joiners...
          </div>
        ) : onboardingCandidates.length === 0 ? (
          <div style={{ background: "#f8fafc", border: "1.5px dashed #cbd5e1", borderRadius: 12, padding: "2rem 1rem", textAlign: "center", color: "#64748b" }}>
            <UserPlus className="w-8 h-8 text-slate-300" style={{ margin: "0 auto 0.5rem" }} />
            <div style={{ fontWeight: 700, fontSize: "0.9rem", color: "#334155" }}>No Candidates in Onboarding Currently</div>
            <div style={{ fontSize: "0.78rem", marginTop: "0.25rem" }}>
              When HR moves candidates to onboarding from HR Reviews, their live onboarding and BGV status will appear here.
            </div>
          </div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "0.85rem" }}>
            {onboardingCandidates.map((c) => {
              const progress = computeProgress(c);
              const bgv = c.onboarding?.bgvStatus || "Not Started";
              return (
                <div key={c.id} className="onboarding-card-item">
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "0.5rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.65rem" }}>
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          background: "#1b4b31",
                          color: "#ffffff",
                          fontWeight: 800,
                          fontSize: "0.85rem",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        {c.name?.slice(0, 2)?.toUpperCase() || "CN"}
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: "0.92rem", color: "#0f172a" }}>
                          {c.name}
                        </div>
                        <div style={{ fontSize: "0.75rem", color: "#64748b" }}>
                          {c.appliedPosition || c.role || "Position"} · <span style={{ color: "#1b4b31", fontWeight: 600 }}>{c.department || "General"}</span>
                        </div>
                      </div>
                    </div>

                    <BgvBadge status={bgv} />
                  </div>

                  {/* Progress Bar */}
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.74rem", fontWeight: 700, marginBottom: "0.25rem", color: "#334155" }}>
                      <span>Checklist Progress</span>
                      <span style={{ color: progress === 100 ? "#059669" : "#1b4b31" }}>{progress}%</span>
                    </div>
                    <div style={{ height: 7, background: "#f1f5f9", borderRadius: 99, overflow: "hidden" }}>
                      <div
                        style={{
                          height: "100%",
                          width: `${progress}%`,
                          background: progress === 100 ? "#059669" : "linear-gradient(90deg, #10b981, #059669)",
                          borderRadius: 99,
                          transition: "width 0.4s ease",
                        }}
                      />
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: "0.25rem", borderTop: "1px solid #f8fafc", fontSize: "0.72rem", color: "#64748b" }}>
                    <span>
                      Initiated: {c.onboarding?.createdDate ? new Date(c.onboarding.createdDate).toLocaleDateString() : "Active"}
                    </span>
                    <button
                      type="button"
                      onClick={() => navigate("/hr/onboarding")}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#1b4b31",
                        fontWeight: 700,
                        cursor: "pointer",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.2rem",
                        padding: 0,
                      }}
                    >
                      View Checklist <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Two-column grid */}
      <div className="ats-grid-2">
        {/* Hiring Funnel by Pipeline Stage */}
        <div className="ats-panel">
          <div className="ats-panel-title">
            <span>📊 Hiring Pipeline Funnel</span>
            <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Live Candidate Distribution</span>
          </div>
          {allStages.map((stg) => {
            const count = stageCounts[stg] || 0;
            const max = Math.max(...Object.values(stageCounts), 1);
            const pct = Math.round((count / max) * 100);
            const color = stg === "Onboarding" ? "#10b981" : stageColors[stg] || "#1b4b31";
            return (
              <div key={stg} className="funnel-bar-row">
                <div className="funnel-lbl">{stg}</div>
                <div className="funnel-track">
                  <div
                    className="funnel-fill"
                    style={{
                      width: `${pct}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
                <div className="funnel-cnt" style={{ color: color }}>
                  {count}
                </div>
              </div>
            );
          })}
        </div>

        {/* Interviews & Department Breakdown */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
          <div className="ats-panel">
            <div className="ats-panel-title">
              <span>📅 Today's Interviews ({todayInterviews.length})</span>
            </div>
            {todayInterviews.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", textAlign: "center", padding: "1rem 0" }}>
                No interviews scheduled for today 🎉
              </div>
            ) : (
              todayInterviews.map((item) => (
                <div key={item.id} style={{ display: "flex", justifyContent: "space-between", padding: "0.5rem 0", borderBottom: "1px solid var(--border)", fontSize: "0.83rem" }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>{item.candidateName || "Candidate"}</div>
                    <div style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>{item.round || "Round 1"} · {item.interviewerName || "Interviewer"}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontWeight: 700, color: "#1b4b31" }}>{item.scheduledTime || item.time || "—"}</div>
                    {item.meetingLink && (
                      <a href={item.meetingLink} target="_blank" rel="noreferrer" style={{ fontSize: "0.72rem", color: "#0ea5e9", textDecoration: "underline" }}>
                        Join Link
                      </a>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="ats-panel">
            <div className="ats-panel-title">
              <span>🏢 Requisitions by Department</span>
            </div>
            {deptEntries.length === 0 ? (
              <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", textAlign: "center", padding: "1rem 0" }}>
                No job requisitions created yet
              </div>
            ) : (
              deptEntries.map(([dept, cnt]) => (
                <div key={dept} className="funnel-bar-row">
                  <div className="funnel-lbl">{dept}</div>
                  <div className="funnel-track">
                    <div className="funnel-fill" style={{ width: `${(cnt / Math.max(...deptEntries.map(([, c]) => c), 1)) * 100}%`, background: "#1b4b31" }} />
                  </div>
                  <div className="funnel-cnt">{cnt}</div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Audit Log / Recent Activity */}
      <div className="ats-panel">
        <div className="ats-panel-title">
          <span>⚡ Recent Recruitment Activity</span>
          <span style={{ fontSize: "0.75rem", color: "var(--text-muted)" }}>Audit Trail</span>
        </div>
        {activityLogs.length === 0 ? (
          <div style={{ color: "var(--text-muted)", fontSize: "0.83rem", textAlign: "center", padding: "1rem 0" }}>
            No activity logged yet
          </div>
        ) : (
          activityLogs.slice(0, 6).map((act) => (
            <div key={act.id} className="activity-row">
              <div className="activity-dot" />
              <div style={{ flex: 1 }}>
                <span style={{ fontWeight: 600 }}>{act.actorName || "User"}</span>{" "}
                <span style={{ color: "var(--text-muted)" }}>
                  {act.action === "STAGE_MOVED"
                    ? `moved candidate "${act.entityName}" from ${act.oldValue} to ${act.newValue}`
                    : act.action || "performed an update"}
                </span>
              </div>
              <div className="activity-time">
                {act.createdAt?.toDate ? act.createdAt.toDate().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : "Recently"}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
